import type { components } from '@social-studio/api-client';
import { api } from '../../lib/api';
import { fieldErrors, problemSlug, type Problem } from './problems';

export type Property = components['schemas']['PropertyDto'];

export interface PropertySnapshot {
  readonly property: Property;
  /** Send back as `If-Match` when saving (spec 20 §5). */
  readonly etag: string | null;
}

export interface PropertyChange {
  name?: string;
  location?: string | null;
  region?: string | null;
  description?: string | null;
  website_url?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  brand_tone?: string | null;
  /** Merge patch: a missing part keeps its value, `null` clears it. */
  address?: components['schemas']['UpdatePropertyAddressDto'];
}

const MERGE_PATCH_HEADERS = { 'Content-Type': 'application/merge-patch+json' } as const;
const PAGE_SIZE = 100;

export class ListLoadError extends Error {
  constructor(readonly status: number) {
    super(`list answered ${status}`);
  }
}

/** Follows `next_cursor` to the end: property pickers treat the list as complete. */
export async function fetchProperties(): Promise<Property[]> {
  const properties: Property[] = [];
  let cursor: string | undefined;
  do {
    const { data, response } = await api.GET('/v1/properties', { params: { query: { limit: PAGE_SIZE, cursor } } });
    if (!response.ok || !data) throw new ListLoadError(response.status);
    properties.push(...data.data);
    cursor = data.next_cursor ?? undefined;
  } while (cursor);
  return properties;
}

export class LoadPropertyError extends Error {
  constructor(readonly status: number) {
    super(`GET /v1/properties/{id} answered ${status}`);
  }
}

export async function loadProperty(id: string): Promise<PropertySnapshot> {
  const { data, response } = await api.GET('/v1/properties/{id}', { params: { path: { id } } });
  if (!response.ok || !data) throw new LoadPropertyError(response.status);
  return { property: data, etag: response.headers.get('ETag') };
}

export type CreatePropertyFailure =
  | { kind: 'fields'; fields: Record<string, string> }
  | { kind: 'nameTaken' | 'unexpected' | 'network' };

export async function createProperty(name: string, region: string): Promise<Property | CreatePropertyFailure> {
  let result;
  try {
    result = await api.POST('/v1/properties', { body: region ? { name, region } : { name } });
  } catch {
    return { kind: 'network' };
  }
  if (result.response.ok && result.data) return result.data;
  const problem = result.error as Problem | undefined;
  const slug = problemSlug(problem);
  if (slug === 'validation-failed') return { kind: 'fields', fields: fieldErrors(problem) };
  if (slug === 'property-name-taken') return { kind: 'nameTaken' };
  return { kind: 'unexpected' };
}

export type SavePropertyFailure =
  | { kind: 'fields'; fields: Record<string, string> }
  | { kind: 'nameTaken' | 'staleVersion' | 'unexpected' | 'network' };

export async function saveProperty(id: string, change: PropertyChange, etag: string | null): Promise<PropertySnapshot | SavePropertyFailure> {
  let result;
  try {
    result = await api.PATCH('/v1/properties/{id}', {
      params: { path: { id } },
      headers: { ...MERGE_PATCH_HEADERS, ...(etag ? { 'If-Match': etag } : {}) },
      body: change,
    });
  } catch {
    return { kind: 'network' };
  }
  if (result.response.ok && result.data) {
    return { property: result.data, etag: result.response.headers.get('ETag') };
  }
  const problem = result.error as Problem | undefined;
  const slug = problemSlug(problem);
  if (slug === 'validation-failed') return { kind: 'fields', fields: fieldErrors(problem) };
  if (slug === 'property-name-taken') return { kind: 'nameTaken' };
  if (slug === 'precondition-failed') return { kind: 'staleVersion' };
  return { kind: 'unexpected' };
}
